import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Organisation, OrganisationUnit } from '../../../Models/Common/Organisation';
import { User } from '../../../Models/FitnessBox/User';
import { NavService } from '../../../Services/nav.service';
import { AppContext } from '../../../Shared/common';
import { CookieManager } from '../../../Shared/cookieManager';
import { UserType } from '../../../Shared/enum';
import { Global } from '../../../Shared/global';
import { AuthService } from '../../../Services/auth.service';
import { OrgService } from '../../../Services/org.service';
import { ThemeService } from '../../../Services/theme.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'top-nav',
  templateUrl: './top-nav.component.html',
  styleUrls: ['./top-nav.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TopNavComponent implements OnInit, OnDestroy {
  @Input() Corp: Organisation;
  @Input() LoggedInUser: User;
  
  selectedUnit: OrganisationUnit = null;
  isAdminUser: boolean = false;
  showBranchSelection: boolean = false;
  isLoggingOut: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    public navService: NavService,
    private appctx: AppContext,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private orgService: OrgService,
    private themeService: ThemeService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // ✅ If inputs not provided, fall back to AppContext/AuthService
    if (!this.Corp || !this.Corp.OrgId) {
      this.Corp = this.appctx.CurrentOrg;
      
      if (!this.Corp || !this.Corp.OrgId) {
        const authOrg = this.authService.getCurrentOrganization();
        if (authOrg) {
          this.Corp = this.convertAuthOrgToAppOrg(authOrg);
        }
      }
    }

    if (!this.LoggedInUser || !this.LoggedInUser.ID) {
      this.LoggedInUser = this.appctx.CurrentUser;
      
      if (!this.LoggedInUser || !this.LoggedInUser.ID) {
        const authUser = this.authService.getCurrentUser();
        if (authUser) {
          this.LoggedInUser = authUser as any;
        }
      }
    }

    // Initialize UI only if we have valid data
    if (this.Corp && this.Corp.OrgId && this.LoggedInUser && this.LoggedInUser.ID) {
      this.SetInitValues();
    } else {
      Global.LogWarning('TopNavComponent: Organization or User data not available on init');
    }

    // ✅ Subscribe to context changes to react to updates
    this.appctx.currentOrgChanged
      .pipe(takeUntil(this.destroy$))
      .subscribe(org => {
        if (org && org.OrgId) {
          this.Corp = org;
          this.SetInitValues();
        }
      });

    this.appctx.currentUserChanged
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (user && user.ID) {
          this.LoggedInUser = user;
          this.SetInitValues();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Convert AuthService Organization to AppOrganization format
   */
  private convertAuthOrgToAppOrg(authOrg: any): Organisation {
    const org = new Organisation();
    org.OrgId = authOrg.OrgId;
    org.OrgName = authOrg.OrgName;
    org.IsActive = authOrg.IsActive;
    org.OrgUnits = authOrg.OrgUnits ? authOrg.OrgUnits.map(unit => ({
      UnitId: unit.UnitId,
      UnitName: unit.UnitName,
      IsActive: unit.IsActive,
      IsSelected: unit.IsSelected || true
    })) : [];
    return org;
  }

  /**
   * Logout handler
   */
  logout(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    Global.Log('[TopNavComponent] Logout initiated');
    this.isLoggingOut = true;
    this.cdr.markForCheck();

    this.authService.logout().subscribe({
      next: () => {
        Global.Log('[TopNavComponent] Logout successful');
        this.isLoggingOut = false;
        this.cdr.markForCheck();
        this.performAppReload();
      },
      error: (error) => {
        Global.LogError('[TopNavComponent] Logout error:', error);
        this.isLoggingOut = false;
        this.cdr.markForCheck();
        this.performAppReload();
        // ✅ Safety fallback: Force navigation to login if app.component doesn't handle it
        // This ensures user can't stay on a protected page after logout
        //setTimeout(() => {
        //  this.router.navigate(['/login']).catch(err => {
        //    Global.LogError('[TopNavComponent] Navigation to login failed:', err);
        //  });
        //}, 500);
      }
    });
  }

  /**
   * ✅ Perform app reload with proper logging
   */
  private performAppReload(): void {
    // ✅ Small delay to ensure any ongoing operations complete
    setTimeout(() => {
      Global.Log('[TopNavComponent] Reloading application');

      // ✅ window.location.reload() reloads the current page
      // This causes a full app restart from app.component.ts
      // Set true to bypass browser cache and get fresh version from server
      window.location.reload();
    }, 100);
  }

  SetInitValues(): void {
    if (!this.Corp || !this.Corp.OrgUnits || this.Corp.OrgUnits.length === 0) {
      Global.LogWarning('SetInitValues: No organization units available');
      this.showBranchSelection = false;
      return;
    }

    const selectedUnitItem = this.Corp.OrgUnits.find(unit => unit.IsSelected);
    if (selectedUnitItem) {
      this.selectedUnit = selectedUnitItem;
    }
    
    this.updateCookie();
    this.showBranchSelection = this.Corp.OrgUnits.length > 1;

    // ✅ Safe check for user type
    if (this.LoggedInUser && this.LoggedInUser.UserType) {
      this.isAdminUser = (this.LoggedInUser.UserType === UserType.ADMIN || this.LoggedInUser.UserType === UserType.OWNER);
    }
  }

  changeBranch(unit: OrganisationUnit): void {
    // ✅ Safe null checks
    if (!this.Corp || !this.Corp.OrgUnits || !unit) {
      Global.LogWarning('changeBranch: Invalid organization or unit');
      return;
    }

    // Update selected status for all units
    this.Corp.OrgUnits.forEach(u => {
      u.IsSelected = (u.UnitId === unit.UnitId);
      
      // Also update in AppContext
      if (this.appctx.CurrentOrg && this.appctx.CurrentOrg.OrgUnits) {
        const contextUnit = this.appctx.CurrentOrg.OrgUnits.find(cu => cu.UnitId === u.UnitId);
        if (contextUnit) {
          contextUnit.IsSelected = u.IsSelected;
        }
      }
    });

    this.selectedUnit = unit;
    this.updateCookie();
    
    // Notify org service of the change
    this.orgService.setCurrentOrganization(this.Corp);

    // Refresh the current route to reload data for the new unit
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  private updateCookie(): void {
    if (this.selectedUnit && this.selectedUnit.UnitId) {
      const cookieMan = new CookieManager();
      cookieMan.setCookie('SelectedUnit', this.selectedUnit.UnitId.toString(), 2);
    }
  }
}
